import { useTranslation } from '../i18n';
import {
    Eyebrow,
    FilterRow,
    ToggleRow,
    ChipGroup,
    SourceFavicon,
    Skeleton,
} from '../primitives';
import type { FacetBundle } from '../api/types';
import { resolveSource } from '../api/sourceRegistry';

export type SortKey = 'changed' | 'recent' | 'name' | 'author' | 'source' | 'kind';
export type SortDir = 'asc' | 'desc';

export interface SortSpec {
    key: SortKey;
    dir: SortDir;
}

/**
 * Filter state under the ADR-0032 model: brand-keyed Source chips,
 * multi-value facet chip groups for type/engine/game, plus install-kind
 * toggles and the mirror-only switch.
 *
 * <para>The <c>sources</c> set holds brand ids (e.g. <c>"ko-fi"</c>,
 * <c>"gumroad"</c>) matching <c>Work.Source</c> server-side. The chip
 * list itself is server-aggregated (see <c>SourceFacet</c>) so it doesn't
 * grow as the infinite-scroll accumulator loads more pages.</para>
 */
export interface CatalogFilterState {
    search: string;
    sources: Set<string>;
    types: Set<string>;
    engines: Set<string>;
    games: Set<string>;
    /** ADR-0038 access axis. free / pay-what-you-want / paid / tip-jar. */
    access: Set<string>;
    /**
     * ADR-0039 locale axis. BCP 47 codes for non-English content. English
     * is implicit and never appears as a chip.
     */
    locales: Set<string>;
    installKinds: Set<string>;
    mirrorOnly: boolean;
    /**
     * Exact-match author filter, driven by the byline-link on the
     * package detail page (and any future "more by this author" chip).
     * Empty string means no filter. Single-value because authors are
     * free text and the natural UX is "show me everything by X" - a
     * multi-author union is rare and the catalog search box covers it.
     */
    author: string;
    sort: SortSpec;
}

export const DEFAULT_FILTERS: CatalogFilterState = {
    search: '',
    sources: new Set(),
    types: new Set(),
    engines: new Set(),
    games: new Set(),
    access: new Set(),
    locales: new Set(),
    installKinds: new Set(),
    mirrorOnly: false,
    author: '',
    sort: { key: 'changed', dir: 'desc' },
};

// Values match the InstallUrlKind enum on the wire. "Iterative" was the v1
// legacy alias for what v2 calls "Indirect" (no direct download, just a
// landing page); the server enum no longer accepts the v1 spelling and
// returns 400 when the SPA sends it.
const INSTALL_IDS = ['Mirror', 'Upstream', 'Indirect', 'Paywalled'] as const;

const SORT_KEYS: ReadonlyArray<SortKey> = ['changed', 'recent', 'name', 'author', 'source', 'kind'];

/**
 * Decode a CatalogFilterState from the query-string portion of a URL.
 * Used to hydrate the catalog on mount so /emporium?type=Preset&source=ko-fi
 * is a first-class bookmarkable surface.
 *
 * <para>Tolerant of unknown / malformed inputs: bad sort keys fall
 * back to the default, missing keys stay empty. Multi-value params
 * (sources, types, engines, games, installKinds) accept either repeated
 * keys (<c>?type=a&type=b</c>) or comma-separated (<c>?type=a,b</c>).
 * The legacy <c>?host=</c> key is also accepted to keep old bookmarks
 * working.</para>
 */
export function parseFiltersFromQuery(search: string): CatalogFilterState {
    const params = new URLSearchParams(search);
    const readSet = (...keys: string[]): Set<string> => {
        const out = new Set<string>();
        for (const key of keys) {
            for (const value of params.getAll(key)) {
                for (const item of value.split(',')) {
                    const trimmed = item.trim();
                    if (trimmed) out.add(trimmed);
                }
            }
        }
        return out;
    };

    const sortRaw = params.get('sort');
    let sort = DEFAULT_FILTERS.sort;
    if (sortRaw) {
        const [keyPart, dirPart] = sortRaw.split(':');
        const key = SORT_KEYS.includes(keyPart as SortKey) ? (keyPart as SortKey) : null;
        if (key) {
            const dir: SortDir =
                dirPart === 'asc' || dirPart === 'desc' ? dirPart : defaultDirFor(key);
            sort = { key, dir };
        }
    }

    return {
        search: params.get('q') ?? '',
        // `host` is the pre-rename key kept for back-compat; new URLs use `source`.
        sources: readSet('source', 'host'),
        types: readSet('type'),
        engines: readSet('engine'),
        games: readSet('game'),
        access: readSet('access'),
        locales: readSet('locale'),
        installKinds: readSet('install'),
        mirrorOnly: params.get('mirroredOnly') === '1',
        author: (params.get('author') ?? '').trim(),
        sort,
    };
}

/**
 * Map a SortKey to the corresponding Work entity field name. The
 * server-side sort grammar uses C# property names because Koan resolves
 * them against the entity type at parse time.
 */
const SORT_FIELDS: Record<SortKey, string> = {
    changed: 'LastChangedAt',
    recent: 'LastSeenAt',
    name: 'Name',
    author: 'Author',
    source: 'Source',
    kind: 'Kind',
};

/**
 * Build a Koan JSON filter for the server-side-capable axes. Returns
 * <c>null</c> when no server-side filter is active so the request stays
 * a bare <c>/api/works?page=N</c>.
 *
 * <para>Top-level fields only (Koan v1 limit): <c>Source</c>, <c>Kind</c>,
 * <c>InstallUrlKind</c>, <c>Name</c>, <c>Author</c>, etc. The tag axes
 * (engine / game / access / locale) are filterable too, but as virtual
 * scope axes rather than real Work properties: <c>WorksController</c>
 * intercepts <c>Engines</c> / <c>Games</c> / <c>Access</c> / <c>Locales</c>
 * server-side and resolves each against the <c>WorkScope</c> projection
 * (ADR-0024 Tranche 6.b.2/D4) into a single <c>Id: {$in: [...]}</c> clause
 * before Koan's generic filter parser ever sees them - Work itself carries
 * no such properties. From here they still compose as <c>$in</c> below, and
 * filtering + pagination stay correct server-side (no client-side residual
 * pass).</para>
 *
 * <para>Search composes as <c>$or</c> across Name + Author +
 * PrimaryPage with <c>*term*</c> wildcards - Koan's JsonFilterBuilder
 * treats <c>*term*</c> as a string Contains match. Source chips compose
 * as <c>$in</c> over the top-level <c>Source</c> brand id.</para>
 */
export function buildKoanFilter(state: CatalogFilterState): string | null {
    const clauses: Record<string, unknown> = {};
    const search = state.search.trim();
    if (search.length > 0) {
        // PrimaryPage was the v1 field name; v2 renamed it to Page.
        // Issuing the v1 name produces a 400 from Koan's filter parser
        // (no such member on Work).
        const wildcard = `*${search}*`;
        clauses.$or = [
            { Name: wildcard },
            { Author: wildcard },
            { Page: wildcard },
        ];
    }
    if (state.sources.size > 0) {
        clauses.Source = { $in: [...state.sources] };
    }
    if (state.mirrorOnly) {
        clauses.InstallUrlKind = 'Mirror';
    } else if (state.installKinds.size > 0) {
        clauses.InstallUrlKind = { $in: [...state.installKinds] };
    }
    // Type chips push directly to the server via Work.Kind (the WorkKind
    // enum, source of truth per ADR-0016). state.types holds lowercase keys
    // ("addon") matching the facet bucket + the catalogKind.* i18n labels;
    // capitalise here to match the C# enum names on the wire ("Addon").
    if (state.types.size > 0) {
        clauses.Kind = {
            $in: [...state.types].map((k) => k.charAt(0).toUpperCase() + k.slice(1)),
        };
    }
    // Engine / Game / Access / Locale chips are virtual filter axes, not real
    // Work properties: WorksController.GetCollection intercepts these four
    // keys server-side (WorkScopeFilterRewriter) and resolves each against
    // the WorkScope projection (ADR-0024 Tranche 6.b.2/D4) into a single
    // Id: {$in: [...]} clause before Koan's generic filter parser ever sees
    // them. Work itself carries no Games/Engines/Access/Locales properties.
    //
    // <para>The <c>$in</c> shape below is the wire contract the rewriter
    // recognizes - any other shape on these keys 400s (Work has no such
    // field for the generic parser to fall back to). Within an axis,
    // <c>$in</c> is union (any selected value matches - multi-select = OR,
    // matching chip semantics). Across axes these remain sibling top-level
    // keys, which JsonFilterParser ANDs together, so multi-axis selections
    // intersect exactly as before.</para>
    if (state.engines.size > 0) clauses.Engines = { $in: [...state.engines] };
    if (state.games.size > 0)   clauses.Games   = { $in: [...state.games] };
    if (state.access.size > 0)  clauses.Access  = { $in: [...state.access] };
    if (state.locales.size > 0) clauses.Locales = { $in: [...state.locales] };
    // Author byline filter - exact match (the byline-link source is
    // the same string we're matching against on the catalog row).
    // The search box does a wildcard Author contains as part of $or;
    // this is the orthogonal "open the catalog already scoped to one
    // creator" path that pairs with the package detail byline link.
    const author = state.author.trim();
    if (author.length > 0) {
        clauses.Author = author;
    }
    if (Object.keys(clauses).length === 0) return null;
    return JSON.stringify(clauses);
}

/**
 * Sort spec for the Koan <c>?sort=</c> query param. Returns
 * <c>null</c> when the state matches the controller's default sort
 * (<c>-LastChangedAt</c>) so the URL stays clean and Koan applies its
 * own default.
 */
export function buildKoanSort(state: CatalogFilterState): string | null {
    const isDefault =
        state.sort.key === DEFAULT_FILTERS.sort.key &&
        state.sort.dir === DEFAULT_FILTERS.sort.dir;
    if (isDefault) return null;
    const prefix = state.sort.dir === 'desc' ? '-' : '';
    return `${prefix}${SORT_FIELDS[state.sort.key]}`;
}

/**
 * Serialise the active CatalogFilterState into a query string suitable
 * for <c>history.replaceState</c>. Defaults are omitted so the URL stays
 * clean when no filter is active (no <c>?sort=changed:desc</c> noise
 * on the bare catalog page).
 */
export function filtersToQueryString(state: CatalogFilterState): string {
    const params = new URLSearchParams();
    if (state.search.trim().length > 0) params.set('q', state.search.trim());
    const writeSet = (key: string, set: Set<string>) => {
        if (set.size === 0) return;
        params.set(key, [...set].sort().join(','));
    };
    writeSet('source', state.sources);
    writeSet('type', state.types);
    writeSet('engine', state.engines);
    writeSet('game', state.games);
    writeSet('access', state.access);
    writeSet('locale', state.locales);
    writeSet('install', state.installKinds);
    if (state.mirrorOnly) params.set('mirroredOnly', '1');
    if (state.author.trim().length > 0) params.set('author', state.author.trim());
    const isDefaultSort =
        state.sort.key === DEFAULT_FILTERS.sort.key &&
        state.sort.dir === DEFAULT_FILTERS.sort.dir;
    if (!isDefaultSort) params.set('sort', `${state.sort.key}:${state.sort.dir}`);
    const s = params.toString();
    return s ? `?${s}` : '';
}

interface Props {
    state: CatalogFilterState;
    onChange: (next: CatalogFilterState) => void;
    facets?: FacetBundle;
    facetsLoading?: boolean;
}

/** Default direction when a fresh column is selected. */
export function defaultDirFor(key: SortKey): SortDir {
    return key === 'changed' || key === 'recent' ? 'desc' : 'asc';
}

export function CatalogFilters({ state, onChange, facets, facetsLoading }: Props) {
    const t = useTranslation();

    const sourceBuckets = facets?.source ?? [];
    const allTypes = facets?.type ?? [];
    const allEngines = facets?.engine ?? [];
    const allGames = facets?.game ?? [];
    const allAccess = facets?.access ?? [];
    const allLocales = facets?.locale ?? [];

    const toggle = <T,>(set: Set<T>, value: T) => {
        const next = new Set(set);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
    };

    return (
        <div class="space-y-6 text-sm">
            <FilterSection title={t('catalogFilters.search')}>
                <input
                    type="search"
                    value={state.search}
                    onInput={(e) =>
                        onChange({ ...state, search: (e.target as HTMLInputElement).value })
                    }
                    placeholder={t('catalogFilters.searchPlaceholder')}
                    autoComplete="off"
                    class="w-full rounded-md border border-rule bg-surface-raised px-3 py-2 text-[13px] text-fg-primary placeholder:text-fg-faint focus:border-accent focus:outline-none focus:ring-[3px] focus:ring-accent-glow"
                />
            </FilterSection>

            {facetsLoading ? (
                <FilterSection title={t('catalogFilters.source')}>
                    <SourceListSkeleton count={5} />
                </FilterSection>
            ) : (
                sourceBuckets.length > 0 && (
                    <FilterSection title={t('catalogFilters.source')}>
                        <ul
                            class="flex flex-col gap-1"
                            role="group"
                            aria-label={t('catalogFilters.source')}
                        >
                            {sourceBuckets.map((bucket) => {
                                const meta = resolveSource(bucket.key);
                                return (
                                    <li key={bucket.key}>
                                        <FilterRow
                                            active={state.sources.has(bucket.key)}
                                            onToggle={() =>
                                                onChange({
                                                    ...state,
                                                    sources: toggle(state.sources, bucket.key),
                                                })
                                            }
                                            leading={<SourceFavicon source={bucket.key} size={14} />}
                                            count={bucket.count}
                                        >
                                            {meta.name}
                                        </FilterRow>
                                    </li>
                                );
                            })}
                        </ul>
                    </FilterSection>
                )
            )}

            {facetsLoading ? (
                <FilterSection title={t('catalogFilters.kind')}>
                    <ChipSkeleton count={4} />
                </FilterSection>
            ) : (
                allTypes.length > 0 && (
                    <FilterSection title={t('catalogFilters.kind')}>
                        <ChipGroup
                            options={allTypes.map((b) => ({
                                id: b.key,
                                label: tagLabel(t, 'catalogKind', b.key),
                            }))}
                            selected={state.types}
                            onToggle={(id) => onChange({ ...state, types: toggle(state.types, id) })}
                            ariaLabel={t('catalogFilters.kind')}
                        />
                    </FilterSection>
                )
            )}

            {facetsLoading ? (
                <FilterSection title={t('catalogFilters.engine')}>
                    <ChipSkeleton count={3} />
                </FilterSection>
            ) : (
                allEngines.length > 0 && (
                    <FilterSection title={t('catalogFilters.engine')}>
                        <ChipGroup
                            options={allEngines.map((b) => ({
                                id: b.key,
                                label: tagLabel(t, 'catalogEngine', b.key),
                            }))}
                            selected={state.engines}
                            onToggle={(id) =>
                                onChange({ ...state, engines: toggle(state.engines, id) })
                            }
                            ariaLabel={t('catalogFilters.engine')}
                        />
                    </FilterSection>
                )
            )}

            {facetsLoading ? (
                <FilterSection title={t('catalogFilters.game')}>
                    <ChipSkeleton count={4} />
                </FilterSection>
            ) : (
                allGames.length > 0 && (
                    <FilterSection title={t('catalogFilters.game')}>
                        <ChipGroup
                            options={allGames.map((b) => ({
                                id: b.key,
                                label: tagLabel(t, 'catalogGame', b.key, b.label),
                            }))}
                            selected={state.games}
                            onToggle={(id) => onChange({ ...state, games: toggle(state.games, id) })}
                            ariaLabel={t('catalogFilters.game')}
                        />
                    </FilterSection>
                )
            )}

            {allAccess.length > 0 && (
                <FilterSection title={t('catalogFilters.access')}>
                    <ChipGroup
                        options={allAccess.map((b) => ({
                            id: b.key,
                            label: tagLabel(t, 'catalogAccess', b.key),
                        }))}
                        selected={state.access}
                        onToggle={(id) =>
                            onChange({ ...state, access: toggle(state.access, id) })
                        }
                        ariaLabel={t('catalogFilters.access')}
                    />
                </FilterSection>
            )}

            {allLocales.length > 0 && (
                <FilterSection title={t('catalogFilters.locale')}>
                    <ChipGroup
                        options={allLocales.map((b) => ({
                            id: b.key,
                            label: tagLabel(t, 'catalogLocale', b.key),
                        }))}
                        selected={state.locales}
                        onToggle={(id) =>
                            onChange({ ...state, locales: toggle(state.locales, id) })
                        }
                        ariaLabel={t('catalogFilters.locale')}
                    />
                </FilterSection>
            )}

            <FilterSection title={t('catalogFilters.install')}>
                <ul
                    class="flex flex-col gap-1"
                    role="group"
                    aria-label={t('catalogFilters.install')}
                >
                    {INSTALL_IDS.map((id) => (
                        <li key={id}>
                            <ToggleRow
                                active={state.installKinds.has(id)}
                                onToggle={() =>
                                    onChange({
                                        ...state,
                                        installKinds: toggle(state.installKinds, id),
                                    })
                                }
                            >
                                {t(`catalogInstall.${id}`)}
                            </ToggleRow>
                        </li>
                    ))}
                </ul>
            </FilterSection>

            <FilterSection title={t('catalogFilters.other')}>
                <ToggleRow
                    active={state.mirrorOnly}
                    onToggle={() => onChange({ ...state, mirrorOnly: !state.mirrorOnly })}
                >
                    {t('catalogFilters.mirroredOnly')}
                </ToggleRow>
            </FilterSection>

            {hasAnyFilter(state) && (
                <button
                    type="button"
                    onClick={() => onChange(DEFAULT_FILTERS)}
                    class="px-1 py-1 font-mono text-[10.5px] uppercase tracking-[0.12em] text-accent transition-colors hover:text-fg-primary"
                >
                    {t('catalogFilters.clearAll')} ✕
                </button>
            )}
        </div>
    );
}

function FilterSection({ title, children }: { title: string; children: import('preact').ComponentChildren }) {
    return (
        <div>
            <Eyebrow as="h3" class="mb-3">
                {title}
            </Eyebrow>
            {children}
        </div>
    );
}

/**
 * Skeleton placeholders for the source list (FilterRow stack) while the
 * facets request is in flight. Matches the real rows' rhythm + height so
 * the sidebar doesn't reflow when the real chips swap in.
 */
function SourceListSkeleton({ count }: { count: number }) {
    return (
        <div class="flex flex-col gap-1" aria-hidden="true">
            {Array.from({ length: count }).map((_, i) => (
                <Skeleton.Line key={i} height="1.75rem" />
            ))}
        </div>
    );
}

/**
 * Skeleton placeholders for a ChipGroup section while the facets request
 * is in flight. The varied pill widths look like real chip content rather
 * than uniform rectangles.
 */
function ChipSkeleton({ count }: { count: number }) {
    const widths = ['3rem', '4.5rem', '3.75rem', '5.25rem', '4rem', '3.25rem'];
    return (
        <div class="flex flex-wrap gap-1.5" aria-hidden="true">
            {Array.from({ length: count }).map((_, i) => (
                <Skeleton.Line key={i} width={widths[i % widths.length]} height="1.5rem" />
            ))}
        </div>
    );
}

function hasAnyFilter(state: CatalogFilterState): boolean {
    return (
        state.search.trim().length > 0 ||
        state.sources.size > 0 ||
        state.types.size > 0 ||
        state.engines.size > 0 ||
        state.games.size > 0 ||
        state.access.size > 0 ||
        state.locales.size > 0 ||
        state.installKinds.size > 0 ||
        state.mirrorOnly ||
        state.sort.key !== 'changed' ||
        state.sort.dir !== 'desc'
    );
}

function tagLabel(
    t: (key: string) => string,
    i18nPrefix: string,
    tagValue: string,
    serverLabel?: string,
): string {
    // i18n translation wins (lets non-English locales override), then the
    // server-resolved label (game axis: Game.DisplayName - the authoritative
    // English label), then a titlecased fallback of the slug.
    const key = `${i18nPrefix}.${tagValue}`;
    const looked = t(key);
    if (looked && looked !== key) return looked;
    if (serverLabel && serverLabel.length > 0) return serverLabel;
    return tagValue
        .split('-')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
}
